// Realtime_Channel client shared by the public homepage and the admin portal (Req 2.4-2.7,
// 2.9). No DOM: the socket factory and timers are injected, so the reconnect state
// machine is unit-tested in Node (site/test/unit/realtimeClient.test.ts).
//
//   connecting --open--> live --close--> reconnecting --(backoff)--> connecting ...
//
// - "reconnecting" is reported at once when an open socket drops (well within the 2 s of
//   Req 2.4) and stays until a socket opens again.
// - Reconnect attempts wait 1 s, doubling after each failure up to 30 s, for as long as
//   the page is open.
// - While disconnected, `poll` (GET /api/state) runs every 10 s; failures keep the last
//   displayed values (the caller only applies successful responses) and polling goes on.
// - When a socket opens after a drop, `resync` fetches a full snapshot (Req 2.5); the
//   server's `hello` message carries one too.
// - `ping` every 25 s keeps the visitor counted as active (design "Realtime hub").

export interface SocketLike {
  send(data: string): void;
  close(code?: number): void;
  addEventListener(type: 'open', fn: () => void): void;
  addEventListener(type: 'message', fn: (ev: { data: unknown }) => void): void;
  addEventListener(type: 'close', fn: (ev: { code: number }) => void): void;
}

export interface RealtimeTimers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

export type RealtimeStatus = 'connecting' | 'live' | 'reconnecting' | 'stopped';

export const PING_MS = 25_000;
export const POLL_MS = 10_000;
export const MAX_BACKOFF_MS = 30_000;

/** Reconnect delay before attempt n (0-based): 1 s doubling to 30 s. */
export function backoffMs(attempt: number): number {
  return Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.max(0, attempt));
}

export interface RealtimeOptions {
  readonly url: string;
  readonly createSocket: (url: string) => SocketLike;
  readonly timers: RealtimeTimers;
  /** Every JSON object message from the server. */
  readonly onMessage: (msg: Record<string, unknown>) => void;
  readonly onStatus?: (status: RealtimeStatus) => void;
  /**
   * Fetches and applies a full snapshot; resolves true on success. Used for the
   * disconnected polling and the resync after a reconnect. Omitted: neither happens.
   */
  readonly poll?: () => Promise<boolean>;
  /** Close code -> true to stop for good (e.g. 4401 "session ended" in the admin). */
  readonly stopOnClose?: (code: number) => boolean;
  readonly pingMs?: number;
  readonly pollMs?: number;
}

export class RealtimeClient {
  private readonly opts: RealtimeOptions;
  private socket: SocketLike | null = null;
  private statusValue: RealtimeStatus = 'connecting';
  private attempt = 0;
  private dropped = false;
  private pingTimer: unknown = null;
  private pollTimer: unknown = null;
  private retryTimer: unknown = null;
  private stopped = false;

  constructor(opts: RealtimeOptions) {
    this.opts = opts;
  }

  get status(): RealtimeStatus {
    return this.statusValue;
  }

  /** Failed attempts since the last open socket. */
  get attempts(): number {
    return this.attempt;
  }

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const s = this.socket;
    this.socket = null;
    s?.close();
    this.setStatus('stopped');
  }

  /** Runs one poll now (e.g. the countdown reached zero); never throws. */
  async refresh(): Promise<boolean> {
    if (!this.opts.poll) return false;
    try {
      return await this.opts.poll();
    } catch {
      return false;
    }
  }

  private setStatus(s: RealtimeStatus): void {
    if (this.statusValue === s) return;
    this.statusValue = s;
    this.opts.onStatus?.(s);
  }

  private open(): void {
    if (this.stopped) return;
    if (!this.dropped) this.setStatus('connecting');
    let socket: SocketLike;
    try {
      socket = this.opts.createSocket(this.opts.url);
    } catch {
      this.onDrop(1006);
      return;
    }
    this.socket = socket;
    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      const wasDropped = this.dropped;
      this.attempt = 0;
      this.dropped = false;
      this.stopPolling();
      this.setStatus('live');
      this.pingTimer = this.opts.timers.setInterval(() => {
        try {
          socket.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // the close event follows
        }
      }, this.opts.pingMs ?? PING_MS);
      if (wasDropped) void this.refresh();
    });
    socket.addEventListener('message', (ev) => {
      if (this.socket !== socket) return;
      let msg: unknown;
      try {
        msg = JSON.parse(String(ev.data));
      } catch {
        return; // ignore malformed messages
      }
      if (typeof msg === 'object' && msg !== null && !Array.isArray(msg)) {
        this.opts.onMessage(msg as Record<string, unknown>);
      }
    });
    socket.addEventListener('close', (ev) => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.onDrop(ev.code);
    });
  }

  private onDrop(code: number): void {
    if (this.pingTimer !== null) this.opts.timers.clearInterval(this.pingTimer);
    this.pingTimer = null;
    if (this.stopped) return;
    if (this.opts.stopOnClose?.(code)) {
      this.stop();
      return;
    }
    this.dropped = true;
    this.setStatus('reconnecting');
    this.startPolling();
    const delay = backoffMs(this.attempt);
    this.attempt += 1;
    this.retryTimer = this.opts.timers.setTimeout(() => {
      this.retryTimer = null;
      this.open();
    }, delay);
  }

  private startPolling(): void {
    if (!this.opts.poll || this.pollTimer !== null) return;
    this.pollTimer = this.opts.timers.setInterval(() => {
      void this.refresh();
    }, this.opts.pollMs ?? POLL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) this.opts.timers.clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private clearTimers(): void {
    if (this.pingTimer !== null) this.opts.timers.clearInterval(this.pingTimer);
    if (this.retryTimer !== null) this.opts.timers.clearTimeout(this.retryTimer);
    this.pingTimer = null;
    this.retryTimer = null;
    this.stopPolling();
  }
}
